<script lang="ts">
  import { get } from 'svelte/store'
  import { errorStore, grammarActions } from '$lib/stores/grammar'
  import { CorrectionApplicationService } from '$lib/services/CorrectionApplicationService'
  import { undoRedoService } from '$lib/services/UndoRedoService'
  import type { AppliedCorrectionInfo } from '$lib/services/UndoRedoService'
  
  /**
   * CorrectionController: Handles correction operations
   * Single Responsibility: Apply, reject, and manage grammar corrections
   */
  
  interface Props {
    editorText: string
    errorState: any
    hasChecked: boolean
    correctionStates: Map<string, AppliedCorrectionInfo>
    lastHistoryText: string | null
    onStatusMessage: (message: string) => void
    onTextUpdate: (text: string, isCorrection?: boolean) => void
    onAllCorrectionsApplied?: (count: number) => void
    onUpdateLastHistoryText?: (text: string) => void
  }
  
  let {
    editorText = $bindable(''),
    errorState,
    hasChecked = $bindable(false),
    correctionStates = $bindable(new Map()),
    lastHistoryText,
    onStatusMessage,
    onTextUpdate,
    onAllCorrectionsApplied,
    onUpdateLastHistoryText
  }: Props = $props()
  
  let historyTimer: ReturnType<typeof setTimeout> | null = null
  
  export function handleApplyCorrection(errorId: string, correctionId: string) {
    // Save current state before applying correction
    const currentText = editorText
    const currentCorrectionStates = new Map(correctionStates)
    const currentErrors = [...errorState.errors]
    
    // Clear any pending history timer to avoid duplicate entries
    if (historyTimer) {
      clearTimeout(historyTimer)
      historyTimer = null
    }
    
    // Use CorrectionApplicationService for all business logic
    const result = CorrectionApplicationService.applyCorrection(
      errorId,
      correctionId,
      currentText
    )
    
    if (result.success) {
      // Update correction states
      const newCorrectionStates = new Map(currentCorrectionStates)
      newCorrectionStates.set(errorId, {
        applied: true,
        correctionId,
        timestamp: Date.now()
      })
      
      // Add correction result to history (single entry with all necessary state)
      undoRedoService.addEntry('correction', result.newText!, newCorrectionStates, {
        description: `교정 적용`,
        correctionCount: 1,
        previousText: currentText
      }, result.remainingErrors || [], hasChecked, errorState.ignoredErrors)
      
      // Update the correction states in the component
      correctionStates = newCorrectionStates
      
      // Update text with correction flag
      onTextUpdate(result.newText!, true)
      
      // Update lastHistoryText to prevent duplicate history entries
      if (onUpdateLastHistoryText) {
        onUpdateLastHistoryText(result.newText!)
      }
      
      // Show status message - check for actionable errors
      // Small delay to ensure store is updated
      setTimeout(() => {
        const freshErrorState = get(errorStore)
        const actionableErrors = freshErrorState.errors.filter(e => 
          e.userAction !== 'kept-original' && 
          !e.isOriginalKept &&
          !freshErrorState.ignoredErrors.has(e.id)
        )
        
        if (actionableErrors.length === 0 && hasChecked) {
          onStatusMessage('모든 오류가 수정되었습니다')
        } else {
          onStatusMessage(result.message || '수정이 적용되었습니다')
        }
      }, 10)
    }
  }
  
  export function handleApplyAlternative(errorId: string, alternativeText: string) {
    // Save current state before applying correction
    const currentText = editorText
    const currentCorrectionStates = new Map(correctionStates)
    const currentErrors = [...errorState.errors]
    
    // Clear any pending history timer to avoid duplicate entries
    if (historyTimer) {
      clearTimeout(historyTimer)
      historyTimer = null
    }
    
    // Use CorrectionApplicationService for all business logic
    const result = CorrectionApplicationService.applyAlternative(
      errorId,
      alternativeText,
      currentText
    )
    
    if (result.success) {
      // Update correction states
      const newCorrectionStates = new Map(currentCorrectionStates)
      newCorrectionStates.set(errorId, {
        applied: true,
        correctionId: 'alternative',
        timestamp: Date.now()
      })
      
      // Add correction result to history (single entry with all necessary state)
      undoRedoService.addEntry('correction', result.newText!, newCorrectionStates, {
        description: `대체 텍스트 적용: ${alternativeText}`,
        correctionCount: 1,
        previousText: currentText
      }, result.remainingErrors || [], hasChecked, errorState.ignoredErrors)
      
      // Update the correction states in the component
      correctionStates = newCorrectionStates
      
      // Update text with correction flag
      onTextUpdate(result.newText!, true)
      
      // Update lastHistoryText to prevent duplicate history entries
      if (onUpdateLastHistoryText) {
        onUpdateLastHistoryText(result.newText!)
      }
      
      // Show status message
      if (!result.remainingErrors || result.remainingErrors.length === 0) {
        onStatusMessage('모든 오류가 수정되었습니다')
      } else {
        onStatusMessage('수정이 적용되었습니다')
      }
    }
  }
  
  export function handleApplyAllCorrections() {
    // Save current state before applying corrections
    const currentText = editorText
    const currentCorrectionStates = new Map(correctionStates)
    const currentErrors = [...errorState.errors]
    const currentIgnoredErrors = new Set(errorState.ignoredErrors)
    
    // Save the original error count BEFORE applying corrections
    // This is important because CorrectionApplicationService will clear the errors
    const originalErrorCount = currentErrors.filter(e => !currentIgnoredErrors.has(e.id)).length
    
    
    // Clear any pending history timer to avoid duplicate entries
    if (historyTimer) {
      clearTimeout(historyTimer)
      historyTimer = null
    }
    
    // Always add current state to history before applying all corrections
    // (This is intentionally always added since it's a bulk operation)
    undoRedoService.addEntry('edit', currentText, currentCorrectionStates, {
      description: '모든 교정 적용 전 상태',
      previousText: lastHistoryText || ''
    }, currentErrors, hasChecked, currentIgnoredErrors)
    
    // Use CorrectionApplicationService for all business logic
    const result = CorrectionApplicationService.applyAllCorrections(currentText)
    
    if (result.success) {
      // Update correction states for all applied corrections and kept-original items
      currentErrors.forEach(error => {
        if (!currentIgnoredErrors.has(error.id)) {
          // Check if this error was kept as original
          const remainingError = (result.remainingErrors || []).find(e => e.id === error.id)
          const isKeptOriginal = remainingError && (remainingError.isOriginalKept || remainingError.userAction === 'kept-original')
          
          if (isKeptOriginal) {
            // Set correction state for kept-original
            correctionStates.set(error.id, {
              applied: true,  // Mark as applied to show it was processed
              correctionId: 'kept-original',  // Special ID for kept-original
              timestamp: Date.now()
            })
          } else if (error.corrections && error.corrections.length > 0) {
            // Normal correction applied
            correctionStates.set(error.id, {
              applied: true,
              correctionId: error.corrections[0].id,
              timestamp: Date.now()
            })
          }
        }
      })
      
      // Add correction result to history  
      undoRedoService.addEntry('bulk_apply', result.newText!, correctionStates, {
        description: '모든 교정 적용',
        correctionCount: result.appliedCount,
        previousText: currentText
      }, result.remainingErrors || [], hasChecked, errorState.ignoredErrors)
      
      // Update text with correction flag
      onTextUpdate(result.newText!, true)
      
      // Update lastHistoryText to prevent duplicate history entries
      if (onUpdateLastHistoryText) {
        onUpdateLastHistoryText(result.newText!)
      }
      
      // Show status message
      onStatusMessage(result.message)
      
      // Clear correction states after applying all (except for kept-original items)
      // Keep correction states for kept-original items
      const keptOriginalIds = new Set((result.remainingErrors || [])
        .filter(e => e.isOriginalKept || e.userAction === 'kept-original')
        .map(e => e.id))
      
      Array.from(correctionStates.keys()).forEach(key => {
        if (!keptOriginalIds.has(key)) {
          correctionStates.delete(key)
        }
      })
      
      // Notify parent that all corrections were applied
      // Use the pre-saved error count since errors are already cleared by CorrectionApplicationService
      if (onAllCorrectionsApplied && originalErrorCount > 0) {
          onAllCorrectionsApplied(originalErrorCount)
      }
      
      // Note: CorrectionApplicationService already cleared errors via grammarActions.setErrors([], false)
    } else {
      onStatusMessage(result.message)
    }
  }
  
  export function handleRejectAllCorrections() {
    // Save current state before rejecting all corrections
    const currentText = editorText
    const currentCorrectionStates = new Map(correctionStates)
    const currentErrors = [...errorState.errors]
    const currentIgnoredErrors = new Set(errorState.ignoredErrors)
    
    // Clear any pending history timer
    if (historyTimer) {
      clearTimeout(historyTimer)
      historyTimer = null
    }
    
    // Add current state to history before rejecting all
    undoRedoService.addEntry('edit', currentText, currentCorrectionStates, {
      description: '모든 교정 거부 전 상태',
      previousText: lastHistoryText || ''
    }, currentErrors, hasChecked, currentIgnoredErrors)
    
    // Count active errors (not ignored, including kept-original ones)
    const activeErrors = errorState.errors.filter(error => 
      !errorState.ignoredErrors.has(error.id) || error.isOriginalKept || error.userAction === 'kept-original'
    )
    const rejectCount = activeErrors.length
    
    if (rejectCount === 0) {
      onStatusMessage('거부할 오류가 없습니다')
      return
    }
    
    // Reject all errors by adding them to ignored set (including kept-original)
    activeErrors.forEach(error => {
      // The ignoreError action will handle clearing kept-original state
      grammarActions.ignoreError(error.id)
    })
    
    // Create new ignored errors set for history
    const newIgnoredErrors = new Set(errorState.ignoredErrors)
    activeErrors.forEach(error => {
      newIgnoredErrors.add(error.id)
    })
    
    // Add rejection to history
    undoRedoService.addEntry('bulk_reject', currentText, currentCorrectionStates, {
      description: '모든 교정 거부',
      rejectCount,
      previousText: currentText
    }, currentErrors, hasChecked, newIgnoredErrors)
    
    // Update lastHistoryText to prevent duplicate history entries
    if (onUpdateLastHistoryText) {
      onUpdateLastHistoryText(currentText)
    }
    
    // Clear correction states after rejecting all
    correctionStates.clear()
    
    // Show status message
    onStatusMessage(`${rejectCount}개의 교정을 모두 거부했습니다`)
  }
  
  export function handleIgnoreError(errorId: string) {
    grammarActions.ignoreError(errorId)
  }
  
  export function handleKeepOriginal(errorId: string) {
    // Toggle keep original status
    const error = errorState.errors.find(e => e.id === errorId)
    if (!error) return

    const isCurrentlyKept = error.userAction === 'kept-original' || error.isOriginalKept

    const updatedErrors = errorState.errors.map(e => {
      if (e.id === errorId) {
        if (isCurrentlyKept) {
          // Remove kept-original status
          const { userAction, isOriginalKept, ...rest } = e
          return rest
        } else {
          // Add kept-original status
          const updated = {
            ...e,
            userAction: 'kept-original' as const,
            isOriginalKept: true
          }
          return updated
        }
      }
      return e
    })

    // Don't merge with AI when toggling keep-original status (use false to avoid merging)
    grammarActions.setErrors(updatedErrors, false)
    
    // Check if there are any actionable errors left
    const actionableErrors = updatedErrors.filter(e => 
      e.userAction !== 'kept-original' && 
      !e.isOriginalKept &&
      !errorState.ignoredErrors.has(e.id)
    )
    
    if (!isCurrentlyKept && actionableErrors.length === 0 && hasChecked) {
      // All errors are now marked as kept-original or ignored
      onStatusMessage('모든 오류 처리가 완료되었습니다')
    } else {
      onStatusMessage(isCurrentlyKept ? '원본 유지를 해제했습니다' : '원본을 유지합니다')
    }
  }
</script>